import { useState } from 'react';
import { Package, Plus, Trash2, ExternalLink, Star } from 'lucide-react';
import { AccountLayout } from '@/components/AccountLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useTechStack, useRemoveFromTechStack } from '@/hooks/useTechStack';
import { useExternalTools, useAddExternalTool, useRemoveExternalTool } from '@/hooks/useExternalTools';
import { optimizeImageUrl } from '@/lib/imageUtils';
import { Link } from 'react-router-dom';
import { ProductPicker } from '@/components/askCircle/ProductPicker';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { addManyToTechStack } from '@/data/techStack';

export default function TechStack() {
  const { data: techStack, isLoading } = useTechStack();
  const { data: externalTools, isLoading: loadingExternal } = useExternalTools();
  const removeFromStack = useRemoveFromTechStack();
  const removeExternalTool = useRemoveExternalTool();
  const addExternalTool = useAddExternalTool();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [showExternalForm, setShowExternalForm] = useState(false);
  const [externalToolForm, setExternalToolForm] = useState({
    toolName: '',
    category: '',
    rating: 0,
    notes: '',
  });

  // Combine tech stack and external tools
  const ownedTools = techStack?.filter(item => item.status === 'owned') || [];
  const triedTools = techStack?.filter(item => item.status === 'tried') || [];
  const ownedExternal = externalTools?.filter(tool => tool.status === 'owned') || [];
  const triedExternal = externalTools?.filter(tool => tool.status === 'tried') || [];

  const totalMonthlyCost = ownedTools
    .filter(item => item.product?.price && item.product?.price_duration === 'mo')
    .reduce((sum, item) => sum + (item.product?.price || 0), 0);

  const handleRemove = (itemId: string) => {
    removeFromStack.mutate(itemId);
  };

  const handleToggleProduct = (product: any) => {
    setSelectedProducts(prev => {
      const exists = prev.some(p => p.id === product.id);
      if (exists) {
        return prev.filter(p => p.id !== product.id);
      }
      return [...prev, product];
    });
  };

  const handleSaveTools = async () => {
    if (!user?.id) return;

    try {
      await addManyToTechStack(
        user.id,
        selectedProducts.map(p => ({ productId: p.id, status: 'owned' }))
      );

      toast.success('Tools added to your stack');
      setIsEditMode(false);
      setSelectedProducts([]);
      
      // Refetch tech stack
      queryClient.invalidateQueries({ queryKey: ['tech-stack'] });
    } catch (error: any) {
      console.error('Error saving tools:', error);
      if (error.code === '23505') {
        toast.error('Some tools are already in your stack');
      } else {
        toast.error('Failed to save tools');
      }
    }
  };

  const handleAddExternalTool = () => {
    if (!externalToolForm.toolName.trim()) {
      toast.error('Please enter a tool name');
      return;
    }

    addExternalTool.mutate({
      toolName: externalToolForm.toolName.trim(),
      category: externalToolForm.category || undefined,
      rating: externalToolForm.rating > 0 ? externalToolForm.rating : undefined,
      notes: externalToolForm.notes.trim() || undefined,
      status: 'owned',
    });

    setExternalToolForm({ toolName: '', category: '', rating: 0, notes: '' });
    setShowExternalForm(false);
  };

  const handleRemoveExternal = (toolId: string) => {
    removeExternalTool.mutate(toolId);
  };

  if (isLoading || loadingExternal) {
    return (
      <AccountLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">My Tools</h1>
            <p className="text-muted-foreground">Loading your tech stack...</p>
          </div>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">My Tools</h1>
            <p className="text-muted-foreground">
              Track the tools you use to get better recommendations from AskCircle
            </p>
          </div>
          <Button onClick={() => setIsEditMode(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Tools
          </Button>
        </div>

        {/* Summary Card */}
        {(ownedTools.length > 0 || ownedExternal.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Stack Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Tools</p>
                  <p className="text-2xl font-bold">{ownedTools.length + ownedExternal.length}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Monthly Cost</p>
                  <p className="text-2xl font-bold">${totalMonthlyCost.toFixed(0)}/mo</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {ownedTools.length === 0 && triedTools.length === 0 && ownedExternal.length === 0 && triedExternal.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No tools yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Add the tools you currently use to get personalized recommendations and avoid redundant suggestions from AskCircle.
              </p>
              <Button onClick={() => setIsEditMode(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Tool
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Tools List */}
        {(ownedTools.length > 0 || triedTools.length > 0 || ownedExternal.length > 0 || triedExternal.length > 0) && (
          <Tabs defaultValue="owned" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="owned">
                My Tools ({ownedTools.length + ownedExternal.length})
              </TabsTrigger>
              <TabsTrigger value="tried">
                Tried ({triedTools.length + triedExternal.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="owned" className="space-y-4 mt-6">
              {ownedTools.length === 0 && ownedExternal.length === 0 ? (
                <EmptyTabMessage message="No tools in use yet" />
              ) : (
                <>
                  {ownedTools.map((item) => (
                    <TechStackItem
                      key={item.id}
                      item={item}
                      onRemove={handleRemove}
                    />
                  ))}
                  {ownedExternal.map((tool) => (
                    <ExternalToolItem
                      key={tool.id}
                      tool={tool}
                      onRemove={handleRemoveExternal}
                    />
                  ))}
                </>
              )}
            </TabsContent>

            <TabsContent value="tried" className="space-y-4 mt-6">
              {triedTools.length === 0 && triedExternal.length === 0 ? (
                <EmptyTabMessage message="No tools tried yet" />
              ) : (
                <>
                  {triedTools.map((item) => (
                    <TechStackItem
                      key={item.id}
                      item={item}
                      onRemove={handleRemove}
                    />
                  ))}
                  {triedExternal.map((tool) => (
                    <ExternalToolItem
                      key={tool.id}
                      tool={tool}
                      onRemove={handleRemoveExternal}
                    />
                  ))}
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Edit Mode Dialog */}
      <Dialog open={isEditMode} onOpenChange={setIsEditMode}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Tools to Your Stack</DialogTitle>
            <DialogDescription>
              Search and select the tools you currently use from our marketplace
            </DialogDescription>
          </DialogHeader>
          
          <ProductPicker
            selectedProducts={selectedProducts}
            onToggleProduct={handleToggleProduct}
          />

          <div className="pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setIsEditMode(false);
                setShowExternalForm(true);
              }}
              className="w-full"
            >
              Don't see your tool? Add it manually
            </Button>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsEditMode(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTools} disabled={selectedProducts.length === 0}>
              Add {selectedProducts.length} Tool{selectedProducts.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* External Tool Form Dialog */}
      <Dialog open={showExternalForm} onOpenChange={setShowExternalForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Custom Tool</DialogTitle>
            <DialogDescription>
              Tell us about a tool you use that's not in our marketplace yet
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="toolName">Tool Name *</Label>
              <Input
                id="toolName"
                placeholder="e.g., Ylopo, BoldTrail, etc."
                value={externalToolForm.toolName}
                onChange={(e) => setExternalToolForm({ ...externalToolForm, toolName: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={externalToolForm.category}
                onValueChange={(value) => setExternalToolForm({ ...externalToolForm, category: value })}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crm">CRM</SelectItem>
                  <SelectItem value="lead-generation">Lead Generation</SelectItem>
                  <SelectItem value="marketing-automation">Marketing Automation</SelectItem>
                  <SelectItem value="websites">Websites</SelectItem>
                  <SelectItem value="coaching">Coaching</SelectItem>
                  <SelectItem value="productivity">Productivity</SelectItem>
                  <SelectItem value="transaction-management">Transaction Management</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Your Rating</Label>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setExternalToolForm({ ...externalToolForm, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`h-6 w-6 ${
                        star <= externalToolForm.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-muted-foreground'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="What do you like or dislike about this tool?"
                value={externalToolForm.notes}
                onChange={(e) => setExternalToolForm({ ...externalToolForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowExternalForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddExternalTool}>
              Add Tool
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AccountLayout>
  );
}

function TechStackItem({ item, onRemove }: any) {
  const product = item.product;

  if (!product) {
    return null;
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Product Image */}
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted shrink-0">
            {product.image ? (
              <img
                src={optimizeImageUrl(product.image, { width: 64, height: 64 })}
                alt={product.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl">
                📦
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base truncate">{product.title}</h3>
                {product.subtitle && (
                  <p className="text-sm text-muted-foreground line-clamp-1">{product.subtitle}</p>
                )}
              </div>
              
              {product.price && (
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold">${product.price}</p>
                  <p className="text-xs text-muted-foreground">
                    {product.price_duration === 'mo' ? '/month' : product.price_duration === 'yr' ? '/year' : ''}
                  </p>
                </div>
              )}
            </div>

            {/* Categories */}
            {product.categories && Array.isArray(product.categories) && product.categories.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {product.categories.slice(0, 3).map((cat: string, i: number) => (
                  <Badge key={i} variant="secondary" className="text-xs">
                    {cat}
                  </Badge>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Link to={`/product/${product.slug}`}>
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View Details
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(item.id)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        </div>

        {/* Notes */}
        {item.notes && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-sm text-muted-foreground">{item.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ExternalToolItem({ tool, onRemove }: any) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted shrink-0 flex items-center justify-center text-2xl">
            📦
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base">{tool.tool_name}</h3>
                <Badge variant="secondary" className="mt-1">
                  Not in marketplace
                </Badge>
              </div>
              {tool.rating && (
                <div className="flex items-center gap-1 shrink-0">
                  {Array.from({ length: tool.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              )}
            </div>

            {tool.category && (
              <div className="mb-3">
                <Badge variant="outline" className="text-xs">
                  {tool.category}
                </Badge>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(tool.id)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        </div>

        {tool.notes && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-sm text-muted-foreground">{tool.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyTabMessage({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-8 text-center">
        <p className="text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  );
}
